import { useState, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import { queryApi } from 'src/api/queryApi';
import { reportsApi } from 'src/api/reportsApi';

const DATE_BUFFER_DAYS = 120;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// All query statuses to fetch
const QUERY_STATUSES = [
  'awaiting',
  'clientApproved',
  'clientNeedsEdits',
  'needsFollowUp',
  'pending',
  'successful',
  'clientPassed',
  'inquireInterest',
  'repurposed',
  'pendingPublication',
  'paidEditorial',
  'journalistPassed',
  'insightsNotIncluded',
];

// In-memory cache (persists across component remounts)
const cache = {
  queries: new Map(), // key: "startDate|endDate" -> { data, timestamp }
  reports: { data: null, timestamp: 0 },
};

const getCacheKey = (startDate, endDate) => `${startDate}|${endDate}`;

const isCacheValid = (timestamp) => Date.now() - timestamp < CACHE_TTL_MS;

// Synchronous cache check - called during initialization
const getInitialState = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return { queries: [], reports: [], hasCache: false };
  }

  const cacheKey = getCacheKey(startDate, endDate);
  const cachedQueries = cache.queries.get(cacheKey);
  const cachedReports = cache.reports;

  const hasValidQueriesCache = cachedQueries && isCacheValid(cachedQueries.timestamp);
  const hasValidReportsCache = cachedReports.data && isCacheValid(cachedReports.timestamp);

  return {
    queries: hasValidQueriesCache ? cachedQueries.data : [],
    reports: hasValidReportsCache ? cachedReports.data : [],
    hasCache: hasValidQueriesCache && hasValidReportsCache,
  };
};

export const useReportsData = (startDate, endDate) => {
  // Check cache synchronously on init - no waiting for async
  const initialState = getInitialState(startDate, endDate);

  const [allQueries, setAllQueries] = useState(initialState.queries);
  const [allReports, setAllReports] = useState(initialState.reports);
  const [isLoading, setIsLoading] = useState(!initialState.hasCache);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  // Add buffer to catch records with different date fields
  const bufferedStartDate = startDate
    ? moment(startDate).subtract(DATE_BUFFER_DAYS, 'days').format('YYYY-MM-DD')
    : null;
  const bufferedEndDate = endDate
    ? moment(endDate).add(DATE_BUFFER_DAYS, 'days').format('YYYY-MM-DD')
    : null;

  const fetchAllData = useCallback(async (forceRefresh = false) => {
    if (!startDate || !endDate) {
      setIsLoading(false);
      return;
    }

    const cacheKey = getCacheKey(startDate, endDate);
    const cachedQueries = cache.queries.get(cacheKey);
    const cachedReports = cache.reports;

    const hasValidQueriesCache = cachedQueries && isCacheValid(cachedQueries.timestamp) && !forceRefresh;
    const hasValidReportsCache = cachedReports.data && isCacheValid(cachedReports.timestamp) && !forceRefresh;

    // If both caches are valid and not forcing refresh, we're done
    if (hasValidQueriesCache && hasValidReportsCache) {
      setAllQueries(cachedQueries.data);
      setAllReports(cachedReports.data);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const promises = [];

      // Fetch queries if not cached
      if (!hasValidQueriesCache) {
        const queryPromises = QUERY_STATUSES.map((status) =>
          queryApi.fetchQueries(status, '20000', {
            // startDate: bufferedStartDate,
            // endDate: bufferedEndDate,
          }).catch(() => [])
        );
        promises.push(Promise.all(queryPromises));
      } else {
        promises.push(Promise.resolve(null));
      }

      // Fetch reports if not cached
      if (!hasValidReportsCache) {
        promises.push(reportsApi.fetchAll().catch(() => []));
      } else {
        promises.push(Promise.resolve(null));
      }

      const [queryResults, reports] = await Promise.all(promises);

      if (!isMounted.current) return;

      if (queryResults) {
        const combinedQueries = queryResults.flat();
        cache.queries.set(cacheKey, { data: combinedQueries, timestamp: Date.now() });
        setAllQueries(combinedQueries);
      }

      if (reports) {
        cache.reports = { data: reports, timestamp: Date.now() };
        setAllReports(reports);
      }
    } catch (err) {
      if (!isMounted.current) return;
      setError(err);
      console.error('Error fetching reports data:', err);
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [startDate, endDate, bufferedStartDate, bufferedEndDate]);

  // Fetch data when dates change
  useEffect(() => {
    isMounted.current = true;

    // Re-check cache when dates change (for subsequent renders)
    const { queries, reports, hasCache } = getInitialState(startDate, endDate);
    if (hasCache) {
      setAllQueries(queries);
      setAllReports(reports);
      setIsLoading(false);
    } else {
      fetchAllData();
    }

    return () => {
      isMounted.current = false;
    };
  }, [startDate, endDate, fetchAllData]);

  // Mutate function to refresh all data (bypasses cache)
  const mutateAll = useCallback(() => {
    fetchAllData(true);
  }, [fetchAllData]);

  // Mutate function for reports only (bypasses cache)
  const allReportsMutate = useCallback(async () => {
    try {
      const reports = await reportsApi.fetchAll();
      cache.reports = { data: reports, timestamp: Date.now() };
      setAllReports(reports);
    } catch (err) {
      console.error('Error refreshing reports:', err);
    }
  }, []);

  return {
    allQueries,
    allReports,
    isLoading,
    error,
    allReportsMutate,
    mutateAll,
  };
};
